import { useState, useEffect } from "react";
import { CUSTOMER_ALIAS_KEY, normCustKey, resolveCustomer } from "./customerAlias";

// #CUST_ALIAS_TOOL_V1 (2026-09-03) — Bill: "JC Comm & JC Communications (Same) and certain there
// are others." Self-contained merge tool, same non-destructive alias pattern as ForkWorklist's DUC
// fork-alias (link/unlink, read-merge-write, confirm before writing). Pass it every raw customer
// name currently in use with a record count via `names` — it lists them, lets you select 2+ and
// pick one canonical spelling, and writes the rest as aliases pointing at it. Every rollup that
// calls resolveCustomer(aliasMap, name) — Job Cost's P&L rollup, Estimating's Customer List/Top-
// by-Quarter — picks the merge up immediately; nothing on the underlying jobs/invoices is touched,
// and any merge can be undone with the × next to it.
// #CUST_DUPFIND_V1 (2026-09-03) — Bill: "chisel on these merges more. Didn't complete them all as
// there were so many." Scanning one long A-Z list by eye doesn't scale. A "Likely duplicates"
// section groups names that share the same first word (A&B Construction / A&B Contractors / A&B
// Contruction all bucket under "A&B") and still have unmerged members, sorted biggest group first —
// so the obvious ones surface immediately instead of needing to be found by scrolling.
export default function CustomerAliasTool({ sg, ss, names, canEdit, onChange, defaultOpen, theme }) {
  // #PNL_BY_CATEGORY_V1 (2026-09-09) — theme-aware now: this tool always rendered white/light even
  // inside Estimating's dark dashboard. `theme` overrides any of the light defaults below; callers
  // that don't pass one (Job Cost) see zero visual change.
  const [alias, setAlias] = useState({});
  const [open, setOpen]   = useState(!!defaultOpen);
  const [sel, setSel]     = useState({});
  const [canonName, setCanonName] = useState('');
  const [q, setQ]         = useState('');

  useEffect(() => { (async () => {
    const al = await sg(CUSTOMER_ALIAS_KEY);
    if (al && typeof al === 'object' && !Array.isArray(al)) { setAlias(al); if (onChange) onChange(al); }
  })(); }, []);

  const C = { card:'#FFFFFF', border:'#E2E8F0', blue:'#1D4ED8', green:'#15803D', red:'#DC2626', text:'#111827', muted:'#6B7280', amber:'#B45309', inputBg:'#FFFFFF', groupHeaderBg:'#FFFBEB', ...(theme||{}) };

  const selNames = Object.keys(sel).filter(n => sel[n]);
  const selCount = selNames.length;

  const toggleSel = (name) => setSel(s => { const n = { ...s }; if (n[name]) delete n[name]; else n[name] = true; return n; });

  const bestGuess = () => {
    if (!selNames.length) return '';
    return [...selNames].sort((a, b) => {
      const ca = (names.find(x => x.name === a) || {}).count || 0;
      const cb = (names.find(x => x.name === b) || {}).count || 0;
      return cb - ca;
    })[0];
  };

  const doMerge = async (canon) => {
    canon = String(canon || '').trim();
    if (!canon || selNames.length < 2) return;
    const nonCanon = selNames.filter(n => normCustKey(n) !== normCustKey(canon));
    if (!window.confirm(`Merge ${selNames.length} names into:\n\n  "${canon}"\n\n${nonCanon.join('\n')}\n\nNothing on the underlying jobs/invoices changes — every report just displays/groups these under "${canon}" from now on. Unmerge any one later with its ×.`)) return;
    const base = (await sg(CUSTOMER_ALIAS_KEY)) || {};
    const next = { ...base };
    nonCanon.forEach(n => { next[normCustKey(n)] = canon; });
    delete next[normCustKey(canon)]; // canon itself, if it was previously merged into something else, now stands as its own canonical spelling
    setAlias(next);
    if (onChange) onChange(next);
    await ss(CUSTOMER_ALIAS_KEY, next);
    setSel({}); setCanonName('');
  };

  const unmerge = async (rawName) => {
    const base = (await sg(CUSTOMER_ALIAS_KEY)) || {};
    const next = { ...base };
    delete next[normCustKey(rawName)];
    setAlias(next);
    if (onChange) onChange(next);
    await ss(CUSTOMER_ALIAS_KEY, next);
  };

  if (!canEdit) return null; // view-only roles don't get the merge tool

  const ql = q.trim().toLowerCase();
  const shown = (names || []).filter(n => !ql || n.name.toLowerCase().includes(ql)).slice().sort((a, b) => a.name.localeCompare(b.name));
  const mergedCount = Object.keys(alias).length;

  // #CUST_DUPFIND_V1 — first-word bucketing: strip punctuation, uppercase, take the first token.
  // Cheap and explainable, and it catches exactly the pattern Bill's own examples show (shared
  // first word, different tail). A bucket only surfaces if it has 2+ raw names AND they don't all
  // already resolve to the same canonical spelling — nothing left to do there otherwise.
  const _bucketKey = (name) => {
    const clean = String(name || '').toUpperCase().replace(/[^A-Z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    return clean.split(' ')[0] || '';
  };
  const dupGroups = (() => {
    const buckets = {};
    (names || []).forEach(n => {
      const key = _bucketKey(n.name);
      if (!key) return;
      (buckets[key] = buckets[key] || []).push(n);
    });
    return Object.entries(buckets)
      .filter(([, arr]) => arr.length >= 2 && new Set(arr.map(n => resolveCustomer(alias, n.name))).size > 1)
      .map(([key, arr]) => ({ key, items: arr.slice().sort((a, b) => b.count - a.count) }))
      .sort((a, b) => b.items.length - a.items.length);
  })();

  const Row = ({ n }) => {
    const canonical = resolveCustomer(alias, n.name);
    const isAliased = canonical !== n.name;
    return (
      // #CUST_ALIAS_ROW_FIX_V1 (2026-09-03) — Bill: rows were "crashing to the right" (name/count
      // squeezed into a narrow wrapped column). A <label> wrapping a checkbox was colliding with
      // the app's global label styling; a plain <div> with pinned checkbox width + explicit flex
      // on the text sidesteps it.
      <div onClick={() => toggleSel(n.name)}
        style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 8px', borderBottom:`1px solid ${C.border}`, fontSize:12, cursor:'pointer', width:'100%', boxSizing:'border-box' }}>
        <input type="checkbox" checked={!!sel[n.name]} onChange={() => toggleSel(n.name)} onClick={e => e.stopPropagation()}
          style={{ flex:'0 0 auto', width:15, height:15, margin:0 }} />
        <span style={{ flex:'1 1 auto', minWidth:0, textAlign:'left', color:C.text }}>{n.name} <span style={{ color:C.muted }}>· {n.count} record{n.count===1?'':'s'}</span></span>
        {isAliased && (
          <span onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:4, flex:'0 0 auto' }}>
            <span style={{ color:C.blue, fontWeight:700, whiteSpace:'nowrap' }}>&rarr; {canonical}</span>
            <button onClick={(e) => { e.preventDefault(); unmerge(n.name); }} title="Unmerge — this name stands alone again"
              style={{ fontSize:11, fontWeight:700, color:C.red, background:'none', border:'none', cursor:'pointer', padding:'0 4px' }}>&times;</button>
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom:12 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:6, cursor:'pointer',
          border:`1px solid ${C.border}`, background:C.inputBg, color:C.muted }}>
        {open ? 'Hide' : 'Clean up'} customer names{mergedCount ? ` (${mergedCount} merged)` : ''}
      </button>
      {open && (
        <div style={{ marginTop:8, background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:10 }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>
            Same customer typed differently across jobs (JC Comm / JC Communications, etc.) splits your
            rollups. Check two or more below and merge them to one name — nothing on the underlying jobs
            changes, this just groups them for reporting. Undo anytime with the &times; next to a merged name.
          </div>

          {dupGroups.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10.5, color:C.amber, fontWeight:800, letterSpacing:0.3, marginBottom:4 }}>
                LIKELY DUPLICATES — {dupGroups.length} group{dupGroups.length===1?'':'s'} to check first
              </div>
              <div style={{ border:`1px solid ${C.border}`, borderRadius:6, overflow:'hidden' }}>
                {dupGroups.map(g => (
                  <div key={g.key} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:C.muted, padding:'4px 8px', background:C.groupHeaderBg }}>
                      Starts with "{g.key}" · {g.items.length} names
                    </div>
                    {g.items.map(n => <Row key={n.name} n={n} />)}
                  </div>
                ))}
              </div>
            </div>
          )}

          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter names…"
            style={{ fontSize:12, padding:'5px 8px', border:`1px solid ${C.border}`, borderRadius:6, marginBottom:8, width:220, boxSizing:'border-box', background:C.inputBg, color:C.text }} />
          <div style={{ fontSize:10.5, color:C.muted, fontWeight:700, marginBottom:4 }}>ALL NAMES (A–Z)</div>
          <div style={{ maxHeight:260, overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:6 }}>
            {shown.map(n => <Row key={n.name} n={n} />)}
            {!shown.length && <div style={{ padding:10, fontSize:12, color:C.muted }}>No customer names on file yet.</div>}
          </div>
          {/* #CUST_MERGEBAR_STICKY_V1 (2026-09-04) — Bill: "choosing but not allowing me the choice
              to merge" on the Customers tab. Root cause: with 210 "likely duplicate" groups on 869
              customers, this bar rendered only after that entire (unscrolled) list — hundreds of rows
              below where he was checking boxes. It was never broken, just scrolled out of reach.
              Sticky-to-bottom keeps it on screen the moment 2+ names are selected, no matter how far
              up the list you're working. */}
          {selCount >= 2 && (() => {
            const suggested = bestGuess();
            const canonVal = canonName || suggested;
            return (
              <div style={{ marginTop:10, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap',
                position:'sticky', bottom:0, zIndex:5, background:C.card, border:`1px solid ${C.blue}`,
                borderRadius:8, padding:'8px 10px', boxShadow:'0 -4px 12px rgba(0,0,0,0.15)' }}>
                <span style={{ fontSize:11.5, fontWeight:700, color:C.text }}>Merge {selCount} names into:</span>
                <input value={canonVal} onChange={e => setCanonName(e.target.value)} placeholder="Canonical name…"
                  style={{ fontSize:12, padding:'5px 8px', border:`1px solid ${C.border}`, borderRadius:6, minWidth:200, background:C.inputBg, color:C.text }} />
                <button onClick={() => doMerge(canonVal)} disabled={!canonVal.trim()}
                  style={{ fontSize:11.5, fontWeight:700, padding:'5px 12px', borderRadius:6, cursor: canonVal.trim() ? 'pointer' : 'not-allowed',
                    border:`1px solid ${C.blue}`, background: canonVal.trim() ? C.blue : C.inputBg, color: canonVal.trim() ? '#fff' : C.muted }}>Merge &rarr;</button>
                <button onClick={() => { setSel({}); setCanonName(''); }}
                  style={{ fontSize:11.5, fontWeight:600, padding:'5px 10px', borderRadius:6, cursor:'pointer', border:`1px solid ${C.border}`, background:C.inputBg, color:C.muted }}>Clear</button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
